import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../state/auth'
import { api } from '../api/http'
import Modal from '../ui/Modal'
import Toast from '../ui/Toast'
import Select from '../ui/Select'
import './projects.css'

export default function Project(){
    const { id } = useParams()
    const navigate = useNavigate()
    const user = useAuth(s => s.user)
    const logout = useAuth(s => s.logout)

    const [loading, setLoading] = useState(true)
    const [err, setErr] = useState(null)
    const [toast, setToast] = useState(null)

    const [project, setProject] = useState(null)
    const [columns, setColumns] = useState([])
    const [members, setMembers] = useState([])

    const [openItem, setOpenItem] = useState(null)
    const [itemForm, setItemForm] = useState({})

    const [inviteOpen, setInviteOpen] = useState(false)
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteRole, setInviteRole] = useState(0)

    const [delOpen, setDelOpen] = useState(false)
    const [delText, setDelText] = useState('')

    const [colModal, setColModal] = useState(false)
    const [colTitle, setColTitle] = useState('')
    const [renameCol, setRenameCol] = useState(null)
    const [delCol, setDelCol] = useState(null)

    const roleNum = Number(project?.role ?? 0)
    const isOwner = !!(project?.owner_id && user?.id && Number(project.owner_id) === Number(user.id))
    const canView = roleNum >= 0 || isOwner
    const canCheck = roleNum >= 1 || isOwner
    const canEdit = roleNum >= 2 || isOwner
    const canAdmin = roleNum >= 3 || isOwner


    const toBool = (v) => v === true || v === 1 || v === '1'

    const ITEM_MIME = 'application/x-todo-item'
    const COL_MIME = 'application/x-todo-col'

    const mapInRole = (r) => {
        const n = Number(r ?? 0)
        return n === 2 ? 3 : n
    }
    const mapOutRole = (r) => {
        const n = Number(r ?? 0)
        return n === 3 ? 2 : n
    }

    async function apiAny(paths, init){
        let lastErr
        for (const p of paths){
            try{ return await api(p, init) }catch(e){ lastErr = e }
        }
        throw lastErr
    }

    const createColumn = async () => {
        if(!canEdit) return
        const t = colTitle.trim()
        if (!t) return
        try{
            const res = await api('/columns/create',{method:'POST',body:JSON.stringify({project_id:Number(id),title:t})})
            setColumns(c => [...c, res.column].sort((a,b)=>a.position-b.position))
            setColTitle('')
            setColModal(false)
        }catch(e){ setToast(e.message||'Create column failed') }
    }

    const saveRename = async () => {
        if (!canEdit || !renameCol) return
        const t = renameCol.title.trim()
        if (!t) return
        try{
            const res = await api('/columns/update',{method:'POST',body:JSON.stringify({id:renameCol.id,title:t})})
            setColumns(cs => cs.map(c => c.id===res.column.id ? {...c,title:res.column.title} : c))
            setRenameCol(null)
        }catch(e){ setToast(e.message||'Rename failed') }
    }

    const confirmDeleteCol = async () => {
        if (!canEdit || !delCol) return
        try{
            await api('/columns/delete',{method:'POST',body:JSON.stringify({id:delCol.id})})
            setColumns(cs => cs.filter(c => c.id!==delCol.id))
            setDelCol(null)
        }catch(e){ setToast(e.message||'Delete column failed') }
    }

    const colDragStart = (e, colId) => {
        if(!canEdit) return
        e.dataTransfer.setData(COL_MIME, JSON.stringify({ colId }))
        e.dataTransfer.effectAllowed = 'move'
    }

    const colDropOn = async (e, overColId) => {
        if(!canEdit) return
        e.preventDefault()
        if (!e.dataTransfer.types.includes(COL_MIME)) return
        const data = JSON.parse(e.dataTransfer.getData(COL_MIME) || '{}')
        if(!data.colId || data.colId===overColId) return
        const order = [...columns].sort((a,b)=>a.position-b.position)
        const from = order.findIndex(c=>c.id===data.colId)
        const to = order.findIndex(c=>c.id===overColId)
        if(from<0||to<0) return
        const [m]=order.splice(from,1)
        order.splice(to,0,m)
        setColumns(order.map((c,i)=>({...c,position:i})))
        try{
            await api('/columns/move',{method:'POST',body:JSON.stringify({id:m.id,to_position:to})})
        }catch(e){ setToast('Move column failed'); load() }
    }

    const onDragStart = (e, itemId, fromColId) => {
        if(!canEdit) return
        e.dataTransfer.setData(ITEM_MIME, JSON.stringify({ itemId, fromColId }))
        e.dataTransfer.effectAllowed = 'move'
    }

    const onDragOver = (e) => {
        if(!canEdit) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
    }

    const dropOnColumn = async (e, toColId) => {
        if(!canEdit) return
        e.preventDefault()
        if (!e.dataTransfer.types.includes(ITEM_MIME)) return
        const data = JSON.parse(e.dataTransfer.getData(ITEM_MIME) || '{}')
        if(!data.itemId) return
        const nextCols = columns.map(c => ({...c, items:[...(c.items||[])]}))
        const fromIdx = nextCols.findIndex(c => c.id===data.fromColId)
        const toIdx = nextCols.findIndex(c => c.id===toColId)
        if(fromIdx<0 || toIdx<0) return
        const fromItems = nextCols[fromIdx].items
        const iIdx = fromItems.findIndex(x => x.id===data.itemId)
        if(iIdx<0) return
        const [moved] = fromItems.splice(iIdx,1)
        const toItems = nextCols[toIdx].items
        const toPosition = toItems.length
        toItems.splice(toPosition, 0, moved)
        setColumns(nextCols)
        try{
            await api('/items/move',{method:'POST',body:JSON.stringify({id:moved.id,to_column_id:toColId,to_position:toPosition})})
        }catch(e){
            setToast('Move failed'); load()
        }
    }

    const dropOnItem = async (e, toColId, overItemId) => {
        if(!canEdit) return
        e.preventDefault()
        if (!e.dataTransfer.types.includes(ITEM_MIME)) return
        const data = JSON.parse(e.dataTransfer.getData(ITEM_MIME) || '{}')
        if(!data.itemId || data.itemId===overItemId) return
        const nextCols = columns.map(c => ({...c, items:[...(c.items||[])]}))
        const fromIdx = nextCols.findIndex(c => c.id===data.fromColId)
        const toIdx = nextCols.findIndex(c => c.id===toColId)
        if(fromIdx<0 || toIdx<0) return
        const fromItems = nextCols[fromIdx].items
        const iIdx = fromItems.findIndex(x => x.id===data.itemId)
        if(iIdx<0) return
        const [moved] = fromItems.splice(iIdx,1)
        const toItems = nextCols[toIdx].items
        const overIdx = toItems.findIndex(x => x.id===overItemId)
        const toPosition = Math.max(0, overIdx)
        toItems.splice(toPosition, 0, moved)
        setColumns(nextCols)
        try{
            await api('/items/move',{method:'POST',body:JSON.stringify({id:moved.id,to_column_id:toColId,to_position:toPosition})})
        }catch(e){
            setToast('Move failed'); load()
        }
    }

    const load = async () => {
        setLoading(true)
        setErr(null)
        try{
            const data = await api(`/projects/get?id=${id}`)
            const pj = {...data.project, role: mapInRole(data.project?.role)}
            setProject(pj)
            setMembers((data.members || []).map(m => ({...m, role: mapInRole(m.role)})))
            const normCols = (data.columns || []).map(c => ({
                ...c,
                items: (c.items || []).map(it => ({
                    ...it,
                    done: toBool(it.done),
                    priority: Number(it.priority ?? 2),
                    subtasks: (it.subtasks || []).map(s => ({ ...s, done: toBool(s.done) })),
                    images: it.images || []
                }))
            }))
            setColumns(normCols)
            setMembers((data.members || []).map(m => ({...m, role: Number(m.role)})))
        }catch(e){
            setErr(e.message || 'Error')
        }finally{
            setLoading(false)
        }
    }

    useEffect(()=>{ load() }, [id])

    const openNewItem = (colId) => {
        if(!canEdit) return
        setOpenItem({id:0,column_id:colId})
        setItemForm({title:'',description:'',color:'',priority:2,due:'',done:false})
    }

    const openEditItem = (it) => {
        setOpenItem(it)
        setItemForm({
            title: it.title || '',
            description: it.description || '',
            color: it.color || '',
            priority: it.priority ?? 2,
            due: it.due ? it.due.slice(0,16) : '',
            done: toBool(it.done)
        })
    }

    const saveItem = async () => {
        if(!canEdit) return
        if(!itemForm.title.trim()) return
        if(openItem.id===0){
            try{
                const res = await api('/items/create',{
                    method:'POST',
                    body:JSON.stringify({
                        column_id: openItem.column_id,
                        title: itemForm.title.trim(),
                        description: itemForm.description || '',
                        color: itemForm.color || '',
                        priority: Number(itemForm.priority) || 0,
                        due: itemForm.due || null,
                        done: !!itemForm.done
                    })
                })
                const next = columns.map(c => {
                    if(c.id!==openItem.column_id) return c
                    return {...c, items:[res.item, ...c.items]}
                })
                setColumns(next)
                setOpenItem(null)
            }catch(e){ setToast(e.message || 'Create failed') }
        }else{
            try{
                const res = await api('/items/update',{method:'POST',body:JSON.stringify({
                        id: openItem.id,
                        title: itemForm.title.trim(),
                        description: itemForm.description || '',
                        color: itemForm.color || '',
                        priority: Number(itemForm.priority)||0,
                        due: itemForm.due || null,
                        done: !!itemForm.done
                    })})
                const next = columns.map(c => ({...c, items: c.items.map(it => it.id===openItem.id ? { ...it, ...res.item, done: toBool(res.item?.done) } : it)}))
                setColumns(next)
                setOpenItem(null)
            }catch(e){ setToast(e.message || 'Update failed') }
        }
    }

    const deleteItem = async () => {
        if(!canEdit || !openItem?.id) return
        try{
            await api('/items/delete',{method:'POST',body:JSON.stringify({id:openItem.id})})
            const next = columns.map(c => ({...c, items:c.items.filter(it => it.id!==openItem.id)}))
            setColumns(next)
            setOpenItem(null)
        }catch(e){ setToast(e.message || 'Delete failed') }
    }

    const addSubtask = async (title) => {
        if(!canEdit || !openItem) return
        const t = String(title||'').trim()
        if(!t) return
        try{
            const res = await api('/subtasks/create',{method:'POST',body:JSON.stringify({item_id:openItem.id,title:t})})
            const newSub = { ...res.subtask, done: toBool(res.subtask?.done) }
            const next = columns.map(c => ({
                ...c,
                items: c.items.map(it => it.id===openItem.id
                    ? { ...it, subtasks:[newSub, ...(it.subtasks||[])] }
                    : it)
            }))
            setColumns(next)
            setItemForm(f => ({...f, subtaskInput:''}))
        }catch(e){ setToast('Add subtask failed') }
    }

    const toggleSubtaskInline = async (itemId, sid) => {
        if (!canCheck) return
        setColumns(cols => cols.map(c => ({
            ...c,
            items: (c.items || []).map(it => it.id === itemId
                ? { ...it, subtasks: (it.subtasks || []).map(s => s.id === sid ? { ...s, done: !s.done } : s) }
                : it
            )
        })))
        try{
            await api('/subtasks/toggle',{ method:'POST', body: JSON.stringify({ id: sid }) })
        }catch(e){
            setToast('Toggle failed')
            load()
        }
    }

    const toggleSubtask = async (sid) => {
        if (!canCheck || !openItem) return
        setColumns(cols => cols.map(c => ({
            ...c,
            items: (c.items || []).map(it => it.id === openItem.id
                ? { ...it, subtasks: (it.subtasks || []).map(s => s.id === sid ? { ...s, done: !s.done } : s) }
                : it
            )
        })))
        try{
            await api('/subtasks/toggle',{ method:'POST', body: JSON.stringify({ id: sid }) })
        }catch(e){
            setToast('Toggle failed')
            load()
        }
    }

    const deleteSubtask = async (sid) => {
        if(!canEdit || !openItem) return
        try{
            await api('/subtasks/delete',{method:'POST',body:JSON.stringify({id:sid})})
            const next = columns.map(c => ({...c, items:c.items.map(it => it.id===openItem.id ? {...it, subtasks:(it.subtasks||[]).filter(s => s.id!==sid)} : it)}))
            setColumns(next)
        }catch(e){ setToast('Delete failed') }
    }

    const changeMemberRole = async (userId, role) => {
        if(!canAdmin) return
        setMembers(ms => ms.map(m => m.user_id===userId ? {...m, role:Number(role)} : m))
        const payload = { project_id: Number(id), user_id: userId, role: mapOutRole(role) }
        try{
            await apiAny(
                [
                    '/projects/member/update',
                    '/projects/members/update',
                    '/members/update',
                    '/projects/updateMember',
                    '/projects/update_member'
                ],
                { method:'POST', body: JSON.stringify(payload) }
            )
        }catch(e){
            setToast(e.message || 'Update role failed')
            load()
        }
    }

    const removeMember = async (userId) => {
        if(!canAdmin) return
        const prev = members
        setMembers(ms => ms.filter(m => m.user_id!==userId))
        const payload = { project_id:Number(id), user_id:userId }
        try{
            await apiAny(
                [
                    '/projects/member/remove',
                    '/projects/members/remove',
                    '/members/remove',
                    '/projects/kick',
                    '/projects/remove_member'
                ],
                { method:'POST', body: JSON.stringify(payload) }
            )
        }catch(e){
            setToast(e.message || 'Remove member failed')
            setMembers(prev)
        }
    }

    const addImageUrl = async (url) => {
        if(!canEdit || !openItem) return
        const u = String(url||'').trim()
        if(!u) return
        try{
            const res = await api('/images/add',{method:'POST',body:JSON.stringify({item_id:openItem.id,url:u})})
            const next = columns.map(c => ({...c, items:c.items.map(it => it.id===openItem.id ? {...it, images:[res.image, ...(it.images||[])]} : it)}))
            setColumns(next)
            setItemForm(f => ({...f, imageInput:''}))
        }catch(e){ setToast('Add image failed') }
    }

    const delImage = async (imgId) => {
        if(!canEdit || !openItem) return
        try{
            await api('/images/delete',{method:'POST',body:JSON.stringify({id:imgId})})
            const next = columns.map(c => ({...c, items:c.items.map(it => it.id===openItem.id ? {...it, images:(it.images||[]).filter(im => im.id!==imgId)} : it)}))
            setColumns(next)
        }catch(e){ setToast('Delete image failed') }
    }

    const invite = async () => {
        if(!canAdmin) return
        const em = inviteEmail.trim()
        if(!em) return
        try{
            const res = await api('/projects/invite',{
                method:'POST',
                body:JSON.stringify({project_id:Number(id),email:em,role:mapOutRole(inviteRole)})
            })

            setMembers(m => [res.member, ...m])
            setInviteEmail('')
            setInviteRole(0)
            setInviteOpen(false)
        }catch(e){ setToast(e.message || 'Invite failed') }
    }

    const toggleItemDone = async (it) => {
        if(!canCheck) return
        const newDone = !it.done
        setColumns(cols => cols.map(c => ({...c, items:(c.items||[]).map(x => x.id===it.id ? {...x, done:newDone} : x)})))
        try{
            await api('/items/update',{ method:'POST', body: JSON.stringify({ id: it.id, done: newDone ? 1 : 0 }) })
        }catch(e){
            setToast('Toggle failed'); load()
        }
    }

    const deleteProject = async () => {
        if(!canAdmin) return
        if(delText !== project?.title) {
            setToast('Delete failed')
            return
        }
        try{
            await api('/projects/delete',{method:'POST',body:JSON.stringify({id:Number(id)})})
            navigate('/projects')
        }catch(e){ setToast(e.message || 'Delete failed') }
    }

    if(loading) return (
        <div className="projects-wrap">
            <header className="projects-header">
                <div className="left">{user?.name || 'Projects'}</div>
                <div className="right">
                    <button className="btn" onClick={()=>navigate('/projects')}>Back</button>
                    <button className="btn danger" onClick={logout}>Logout</button>
                </div>
            </header>
            <main className="projects-main"><div className="muted">Loading…</div></main>
        </div>
    )

    if(err || !canView) return (
        <div className="projects-wrap">
            <header className="projects-header">
                <div className="left">{user?.name || 'Projects'}</div>
                <div className="right">
                    <button className="btn" onClick={()=>navigate('/projects')}>Back</button>
                    <button className="btn danger" onClick={logout}>Logout</button>
                </div>
            </header>
            <main className="projects-main"><div className="error">{err || 'No access'}</div></main>
        </div>
    )

    const editDisabled = !canEdit

    return (
        <div className="projects-wrap">
            <header className="projects-header">
                <div className="left"><i className="fox-icon fox-icon-folder"></i> {project?.title || 'Project'}</div>
                <div className="right">
                    {canAdmin && <button className="btn" onClick={()=>setInviteOpen(true)}><i className="fox-icon fox-icon-users"></i> Invite</button>}
                    {canAdmin && <button className="btn danger" onClick={()=>setDelOpen(true)}><i className="fox-icon fox-icon-trash"></i> Delete Project</button>}
                    <button className="btn" onClick={()=>navigate('/projects')}><i className="fox-icon fox-icon-arrow-left-line"></i> Back</button>
                    <button className="btn danger" onClick={logout}><i className="fox-icon fox-icon-logout-basic"></i> Logout</button>
                </div>
            </header>

            <main className="project-board">
                <div className="board-scroll">
                    <div className="board">
                        {columns.sort((a,b)=>a.position-b.position).map(col=>(
                            <div
                                key={col.id}
                                className="col"
                                draggable={false}
                                onDragOver={e=>{if(canEdit){e.preventDefault()}}}
                                onDrop={e=>{
                                    if(!canEdit) return
                                    if (e.dataTransfer.types.includes(COL_MIME)) colDropOn(e,col.id)
                                    else if (e.dataTransfer.types.includes(ITEM_MIME)) dropOnColumn(e,col.id)
                                }}
                            >
                                <div
                                    className="col-head"
                                    draggable={canEdit}
                                    onDragStart={e=>colDragStart(e,col.id)}
                                >
                                    <button className="col-title-btn" onClick={()=>{ if(canEdit) setRenameCol({id:col.id,title:col.title}) }}>{col.title}</button>
                                    <div className="col-actions">
                                        {canEdit && <button className="mini" onClick={()=>openNewItem(col.id)}><i className="fox-icon fox-icon-plus-line"></i></button>}
                                        {canEdit && <button className="mini danger" onClick={()=>setDelCol(col)}><i className="fox-icon fox-icon-x-line"></i></button>}
                                    </div>
                                </div>

                                <div className="col-items">
                                    {col.items.map(it=>{
                                        const cleanDesc = String(it.description ?? '')
                                            .replace(/<[^>]*>/g,'')
                                            .trim()
                                            .toLowerCase()
                                        const hasDesc = !!(cleanDesc && cleanDesc !== 'null' && cleanDesc !== 'undefined')
                                        const hasSubs = Array.isArray(it.subtasks) && it.subtasks.length > 0
                                        const hasImgs = Array.isArray(it.images) && it.images.length > 0

                                        return (
                                            <div
                                                key={it.id}
                                                className={`card ${it.done?'done':''}`}
                                                draggable={canEdit}
                                                onDragStart={e=>onDragStart(e,it.id,col.id)}
                                                onDragOver={onDragOver}
                                                onDrop={e=>dropOnItem(e,col.id,it.id)}
                                                onClick={()=>openEditItem(it)}
                                            >
                                                {it.color && <div className="card-accent" style={{ background: it.color }} />}
                                                <div className="card-top">
                                                    {canCheck && (
                                                        <button
                                                            className={`chk ${it.done ? 'on' : ''}`}
                                                            aria-pressed={!!it.done}
                                                            onClick={(e)=>{ e.stopPropagation(); toggleItemDone(it) }}
                                                        />
                                                    )}
                                                    <div className="card-title">{it.title}</div>
                                                </div>

                                                <div className="card-meta">
                                                    {typeof it.priority !== 'undefined' && it.priority !== 2 && (
                                                        <span className={`prio p${it.priority}`}>P{it.priority}</span>
                                                    )}
                                                    {it.due && <span className="due">{new Date(it.due).toLocaleDateString()}</span>}
                                                </div>

                                                {(hasDesc || hasSubs || hasImgs) && (
                                                    <div className="card-flags">
                                                        {hasDesc && <span className="flag" title="Description"><i className="fox-icon fox-icon-folder-document"></i></span>}
                                                        {hasSubs && <span className="flag" title="Subtasks"><i className="fox-icon fox-icon-menu-list"></i></span>}
                                                        {hasImgs && <span className="flag" title="Images"><i className="fox-icon fox-icon-camera"></i>️</span>}
                                                    </div>
                                                )}
                                                {hasImgs && (
                                                    <div className="card-thumbs">
                                                        {it.images.slice(0,3).map(im=>(
                                                            <img key={im.id||im.url} src={im.url} alt="" className="img-fluid" />
                                                        ))}
                                                    </div>
                                                )}
                                                {hasSubs && (
                                                    <ul className="sub-list inline" onClick={(e)=>e.stopPropagation()}>
                                                        {it.subtasks.map(s=>(
                                                            <li key={s.id} className={`sub ${s.done?'done':''}`}>
                                                                <label>
                                                                    {canCheck && (
                                                                        <button
                                                                            className={`subchk ${s.done ? 'on' : ''}`}
                                                                            aria-pressed={!!s.done}
                                                                            onClick={()=>toggleSubtaskInline(it.id, s.id)}
                                                                        />
                                                                    )}
                                                                    <span> {s.title}</span>
                                                                </label>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )})}
                                </div>
                            </div>
                        ))}
                        {canEdit && (
                            <div className="col add-col">
                                <button className="btn primary wfull" onClick={()=>setColModal(true)}>+ Add column</button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <Modal open={colModal} title="New column" onClose={()=>setColModal(false)} onSubmit={createColumn} submitLabel="Create">
                <input placeholder="Column title" value={colTitle} onChange={e=>setColTitle(e.target.value)} />
            </Modal>

            <Modal open={!!renameCol} title="Rename column" onClose={()=>setRenameCol(null)} onSubmit={saveRename} submitLabel="Save">
                <input placeholder="Column title" value={renameCol?.title||''} onChange={e=>setRenameCol(c=>({...c,title:e.target.value}))} />
            </Modal>

            <Modal open={!!delCol} title="Delete column" onClose={()=>setDelCol(null)} onSubmit={confirmDeleteCol} submitLabel="Delete">
                <div className="muted">This will delete the column and all its items.</div>
            </Modal>

            <Modal open={!!openItem} title={openItem?.id===0?'New item':'Edit item'} onClose={()=>setOpenItem(null)} onSubmit={saveItem} submitLabel={openItem?.id===0?'Create':(canEdit?'Save':'Close')}>
                <div className="form-row">
                    <input placeholder="Title" value={itemForm.title||''} onChange={e=>setItemForm(f=>({...f,title:e.target.value}))} disabled={editDisabled}/>
                </div>
                <div className="form-row">
                    <textarea placeholder="Description" value={itemForm.description||''} onChange={e=>setItemForm(f=>({...f,description:e.target.value}))} disabled={editDisabled}/>
                </div>
                <div className="form-row two">
                    <div className="color-wrap" style={{display:'grid',gridTemplateColumns:'auto 80px',gap:'10px',alignItems:'stretch'}}>
                        <input type="color" value={itemForm.color||'#000000'} onChange={e=>setItemForm(f=>({...f,color:e.target.value}))} disabled={editDisabled}/>
                        <button className="btn" onClick={()=>setItemForm(f=>({...f,color:''}))} disabled={editDisabled}>Reset</button>
                    </div>
                    <Select
                        value={itemForm.priority ?? 2}
                        onChange={(v)=>setItemForm(f=>({...f,priority:Number(v)}))}
                        options={[
                            {value:0,label:'Critical priority'},
                            {value:1,label:'High priority'},
                            {value:2,label:'Normal priority'},
                            {value:3,label:'Low priority'}
                        ]}
                        disabled={editDisabled}
                    />
                </div>

                <div className="form-row">
                    <input type="datetime-local" value={itemForm.due||''} onChange={e=>setItemForm(f=>({...f,due:e.target.value}))} disabled={editDisabled}/>
                </div>
                {openItem?.id>0 && (
                    <>
                        <div className="subtasks">
                            <div className="sub-head">Subtasks</div>
                            {canEdit && (
                                <div className="sub-new">
                                    <input placeholder="New subtask" value={itemForm.subtaskInput||''} onChange={e=>setItemForm(f=>({...f,subtaskInput:e.target.value}))} onKeyDown={e=>{ if(e.key==='Enter') addSubtask(itemForm.subtaskInput) }}/>
                                    <button className="btn" onClick={()=>addSubtask(itemForm.subtaskInput)}>Add</button>
                                </div>
                            )}
                            <ul className="sub-list">
                                {(columns.find(c=>c.items.some(i=>i.id===openItem.id))?.items.find(i=>i.id===openItem.id)?.subtasks||[]).map(s=>(
                                    <li key={s.id} className={`sub ${s.done?'done':''}`}>
                                        <label>
                                            {canCheck && (
                                                <button
                                                    className={`subchk ${s.done ? 'on' : ''}`}
                                                    aria-pressed={!!s.done}
                                                    onClick={()=>toggleSubtask(s.id)}
                                                />
                                            )}
                                            <span>{s.title}</span>
                                        </label>
                                        {canEdit && (
                                            <button
                                                className="icon-btn danger"
                                                aria-label="Delete subtask"
                                                onClick={()=>deleteSubtask(s.id)}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="images mt">
                            <div className="sub-head">Images</div>
                            {canEdit && (
                                <div className="sub-new">
                                    <input placeholder="Image URL" value={itemForm.imageInput||''} onChange={e=>setItemForm(f=>({...f,imageInput:e.target.value}))} onKeyDown={e=>{ if(e.key==='Enter') addImageUrl(itemForm.imageInput) }}/>
                                    <button className="btn" onClick={()=>addImageUrl(itemForm.imageInput)}>Add</button>
                                </div>
                            )}
                            <div className="img-list mt">
                                {(columns.find(c=>c.items.some(i=>i.id===openItem.id))?.items.find(i=>i.id===openItem.id)?.images||[]).map(im=>(
                                    <div key={im.id} className="img">
                                        <img src={im.url} alt="" className="img-fluid"/>
                                        {canEdit && <button className="mini danger" onClick={()=>delImage(im.id)}>✕</button>}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {canEdit && <div className="danger-row mt">
                            <button className="btn danger" onClick={deleteItem}>Delete item</button>
                        </div>}
                    </>
                )}
            </Modal>

            <Modal open={inviteOpen} title="Invite member" onClose={()=>setInviteOpen(false)} onSubmit={invite} submitLabel="Invite">
                <div className="form-row inline-2">
                    <input placeholder="E-mail" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} />
                    <Select
                        value={Number(inviteRole)}
                        onChange={(v)=>setInviteRole(Number(v))}
                        options={[
                            {value:0,label:'Viewer'},
                            {value:1,label:'Checker'},
                            {value:2,label:'Editor'},
                            {value:3,label:'Admin'},
                        ]}
                        width="180px"
                    />
                </div>

                {!!members.length && (
                    <div className="members-list">
                        {members.map(m=>{
                            const isOwner = m.owner===1 || (project?.owner_id && m.user_id===project.owner_id)
                            return (
                                <div key={m.user_id} className="member-row">
                                    <div className="m-email">{m.email}</div>
                                    <div className="m-actions">
                                        {isOwner ? (
                                            <div className="role-pill">Owner</div>
                                        ) : (
                                            <>
                                                <Select
                                                    value={Number(m.role)}
                                                    onChange={(v)=>changeMemberRole(m.user_id, Number(v))}
                                                    options={[
                                                        {value:0,label:'Viewer'},
                                                        {value:1,label:'Checker'},
                                                        {value:2,label:'Editor'},
                                                        {value:3,label:'Admin'},
                                                    ]}
                                                    width="160px"
                                                />
                                                <button className="mini danger" onClick={()=>removeMember(m.user_id)}>Remove</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </Modal>

            <Modal open={delOpen} title="Delete project" onClose={()=>setDelOpen(false)} onSubmit={deleteProject} submitLabel="Delete">
                <div className="form-row">
                    <div className="muted">Type <b>{project?.title}</b> to confirm deleting the project.</div>
                    <input placeholder={project?.title} value={delText} onChange={e=>setDelText(e.target.value)} />
                </div>
            </Modal>

            {toast && <Toast text={toast} onClose={()=>setToast(null)} />}
        </div>
    )
}
